/* Menu Entry Template:
{
  name: 'Name that appears in menu',
  id: 'Unique identifier for specific tile',
  content: [{
    id: 'data-src-ID',
    type: 'line/bar/dotplot/table/image'
  }],
  components: ['toggles', 'tabs']
},
*/

export default {
  label: 'TONES 3',
  id: 'tones-3',
  sidebar: [
    {
      li: 'Study Overview',
      subList: [
        {
          name: 'Summary',
          tileId: 'tones-3-summary',
          content: [
            {
              id: 'tones-3-summary',
              type: 'image',
            },
          ],
          components: [],
          tooltip: `
            <p>Inclusion Criteria: participants without current primary OSA therapy use or history of surgical intervention to treat underlying
            obstruction were required to have tried to use primary OSA therapy for at least 1 month with at least one documented adjustment to
            the therapy; baseline ESS ≥10; baseline sleep latency <30 min for average of the first four of a five-trial, 40-min MWT; usual nightly
            sleep time ≥6 h.</p>

            <p>Exclusion Criteria: participants who did not have a usual nightly total sleep time of at least <span style="white-space: nowrap">6 h</span>
            or who had a usual bedtime later than 1:00 A.M.; participants with an occupation requiring nighttime shift work or variable shift work;
            use of any over-the-counter or prescription medications that could affect the evaluation of ES; current or past (within the past 2 years)
            diagnosis of moderate or severe substance use disorder according to DSM-5 criteria; nicotine dependence that has an effect on sleep
            (eg, a subject who routinely awakens at night to smoke); any other clinically relevant medical, behavioral, or psychiatric disorder
            other than OSA that is associated with ES.</p>
          `,
        },
        {
          name: 'Study Design',
          tileId: 'tones-3-study-design',
          content: [
            {
              id: 'tones-3-study-design',
              type: 'image',
            },
          ],
          components: [],
        },
        {
          name: 'Participant Disposition',
          tileId: 'tones-3-participant-disposition',
          content: [
            {
              id: 'tones-3-participant-disposition',
              type: 'image',
            },
          ],
          components: [],
        },
        {
          name: 'Baseline Characteristics',
          tileId: 'tones-3-baseline-characteristics',
          content: [
            {
              id: 'tones-3-baseline-char',
              type: 'table',
            },
          ],
          components: [],
          hideTopScrollEffect: true,
          hideLeftScrollEffect: true,
        },
      ],
    },
    {
      li: 'Efficacy',
      subList: [
        {
          name: 'MWT: mITT',
          tileId: 'tones-3-mwt-mitt',
          content: [
            {
              id: 'tones-3-mwt-mitt',
              type: 'line',
            },
          ],
          components: ['toggles'],
        },
        {
          name: 'MWT: Individual Trials',
          tileId: 'tones-3-mwt-individual-trials',
          content: [
            {
              id: 'tones-3-mwt-indiv',
              type: 'line',
            },
          ],
          components: ['toggles'],
        },
        {
          name: 'ESS',
          tileId: 'tones-3-ess',
          content: [
            {
              id: 'tones-3-ess-mitt',
              type: 'line',
            },
          ],
          components: ['toggles'],
        },
        {
          name: 'PGI-C and CGI-C',
          tileId: 'tones-3-pgi-c-and-cgi-c',
          content: [
            {
              id: 'tones-3-pgic',
              type: 'bar',
              tabTitle: 'PGI-C',
            },
            {
              id: 'tones-3-cgic',
              type: 'bar',
              tabTitle: 'CGI-C',
            },
          ],
          components: ['tabs'],
        },
        {
          name: 'FOSQ-10',
          tileId: 'tones-3-fosq-10',
          content: [
            {
              id: 'tones-3-fosq10',
              type: 'bar',
            },
          ],
          components: [],
        },
        {
          name: 'WPAI',
          tileId: 'tones-3-wpai',
          content: [
            {
              id: 'tones-3-wpai-abs',
              type: 'image',
              resource: () =>
                import('@/assets/cardImgs/tones-3-wpai-absenteeism.svg?inline'),
            },
            {
              id: 'tones-3-wpai-pres',
              type: 'image',
              resource: () =>
                import(
                  '@/assets/cardImgs/tones-3-wpai-presenteeism.svg?inline'
                ),
            },
            {
              id: 'tones-3-wpai-workimp',
              type: 'image',
              resource: () =>
                import(
                  '@/assets/cardImgs/tones-3-wpai-overall-work-impairment.svg?inline'
                ),
            },
            {
              id: 'tones-3-wpai-actimp',
              type: 'image',
              resource: () =>
                import(
                  '@/assets/cardImgs/tones-3-wpai-activity-impairment.svg?inline'
                ),
            },
          ],
          components: [],
          enableScroll: false,
        },
      ],
    },
    {
      li: 'Safety',
      subList: [
        {
          name: 'AEs and TEAEs',
          tileId: 'tones-3-adverse-events-aes-and-teaes',
          content: [
            {
              id: 'tones-3-aes-teaes',
              type: 'table',
            },
          ],
          components: [],
          hideTopScrollEffect: true,
          hideLeftScrollEffect: true,
        },
        {
          name: 'AE Incidence',
          tileId: 'tones-3-adverse-events-incidence',
          content: [
            {
              id: 'tones-3-ae-incidence-anxiety',
              type: 'bar',
              tabTitle: 'Anxiety',
            },
            {
              id: 'tones-3-ae-incidence-appetite',
              type: 'bar',
              tabTitle: 'Decreased appetite',
            },
            {
              id: 'tones-3-ae-incidence-headache',
              type: 'bar',
              tabTitle: 'Headache',
            },
            {
              id: 'tones-3-ae-incidence-insomnia',
              type: 'bar',
              tabTitle: 'Insomnia',
            },
            {
              id: 'tones-3-ae-incidence-jittery',
              type: 'bar',
              tabTitle: 'Feeling jittery',
            },
            {
              id: 'tones-3-ae-incidence-nausea',
              type: 'bar',
              tabTitle: 'Nausea',
            },
          ],
          components: ['tabs'],
        },
        {
          name: 'AE Duration',
          tileId: 'tones-3-adverse-events-duration',
          content: [
            {
              id: 'tones-3-ae-duration',
              type: 'table',
            },
          ],
          components: [],
          hideTopScrollEffect: true,
          hideLeftScrollEffect: true,
        },
        {
          name: 'Vital Signs: BP and HR',
          tileId: 'tones-3-vital-signs-bp-and-hr',
          content: [
            {
              id: 'tones-3-bp-hr',
              type: 'table',
            },
          ],
          components: [],
        },
        {
          name: 'Vital Signs: 24 h SBP',
          tileId: 'tones-3-vital-signs-24h-sbp',
          content: [
            {
              id: 'tones-3-24h-sbp',
              type: 'line',
            },
          ],
          tooltip: `
            <p>Sample size values for effects of solriamfetol on SBP as measured by 24 h ABPM<sup>19</sup></p>

            <table style="width: 100%">
            <tr>
              <td>Treatment Group</td>
              <td style="width: 6.7%">6 AM - 8 AM</td>
              <td style="width: 6.7%">8 AM - 10 AM</td>
              <td style="width: 6.7%">10 AM - 12 PM</td>
              <td style="width: 6.7%">12 PM - 2 PM</td>
              <td style="width: 6.7%">2 PM - 4 PM</td>
              <td style="width: 6.7%">4 PM - 6 PM</td>
              <td style="width: 6.7%">6 PM - 8 PM</td>
              <td style="width: 6.7%">8 PM - 10 PM</td>
              <td style="width: 6.7%">10 PM - 12 AM</td>
              <td style="width: 6.7%">12 AM - 2 AM</td>
              <td style="width: 6.7%">2 AM - 4 AM</td>
              <td style="width: 6.7%">4 AM - 6 AM</td>
            </tr>
            <tr>
              <td>Placebo at screening, n</td>
              <td>116</td>
              <td>116</td>
              <td>115</td>
              <td>116</td>
              <td>116</td>
              <td>113</td>
              <td>115</td>
              <td>113</td>
              <td>116</td>
              <td>116</td>
              <td>116</td>
              <td>114</td>
            </tr>
            <tr>
            <td>Placebo at week 8, n</td>
              <td>92</td>
              <td>92</td>
              <td>94</td>
              <td>94</td>
              <td>93</td>
              <td>91</td>
              <td>93</td>
              <td>91</td>
              <td>93</td>
              <td>94</td>
              <td>93</td>
              <td>92</td>
            </tr>
            <tr>
            <td>Solriamfetol 37.5 mg at screening, n</td>
              <td>56</td>
              <td>55</td>
              <td>53</td>
              <td>54</td>
              <td>55</td>
              <td>60</td>
              <td>53</td>
              <td>55</td>
              <td>57</td>
              <td>57</td>
              <td>57</td>
              <td>57</td>
            </tr>
            <tr>
            <td>Solriamfetol 37.5 mg at week 8, n</td>
              <td>39</td>
              <td>40</td>
              <td>42</td>
              <td>43</td>
              <td>43</td>
              <td>42</td>
              <td>43</td>
              <td>42</td>
              <td>43</td>
              <td>42</td>
              <td>41</td>
              <td>42</td>
            </tr>
            <tr>
            <td>Solriamfetol 75 mg at screening, n</td>
              <td>58</td>
              <td>59</td>
              <td>59</td>
              <td>59</td>
              <td>60</td>
              <td>60</td>
              <td>61</td>
              <td>60</td>
              <td>61</td>
              <td>59</td>
              <td>60</td>
              <td>58</td>
            </tr>
            <tr>
            <td>Solriamfetol 75 mg at week 8,
            n</td>
              <td>51</td>
              <td>50</td>
              <td>51</td>
              <td>47</td>
              <td>49</td>
              <td>48</td>
              <td>47</td>
              <td>47</td>
              <td>48</td>
              <td>49</td>
              <td>49</td>
              <td>49</td>
            </tr>
            <tr>
            <td>Solriamfetol 150 mg at screening, n</td>
              <td>108</td>
              <td>109</td>
              <td>111</td>
              <td>112</td>
              <td>112</td>
              <td>114</td>
              <td>113</td>
              <td>113</td>
              <td>114</td>
              <td>113</td>
              <td>111</td>
              <td>111</td>
            </tr>
            <tr>
            <td>Solriamfetol 150 mg at week 8,
            n</td>
              <td>97</td>
              <td>97</td>
              <td>97</td>
              <td>98</td>
              <td>98</td>
              <td>97</td>
              <td>100</td>
              <td>102</td>
              <td>100</td>
              <td>99</td>
              <td>99</td>
              <td>99</td>
            </tr>
            <tr>
            <td>Solriamfetol 300 mg at screening, n</td>
              <td>107</td>
              <td>107</td>
              <td>106</td>
              <td>109</td>
              <td>109</td>
              <td>111</td>
              <td>108</td>
              <td>109</td>
              <td>109</td>
              <td>108</td>
              <td>107</td>
              <td>109</td>
            </tr>
            <tr>
            <td>Solriamfetol 300 mg at week 8, n</td>
              <td>87</td>
              <td>84</td>
              <td>84</td>
              <td>87</td>
              <td>88</td>
              <td>87</td>
              <td>86</td>
              <td>87</td>
              <td>87</td>
              <td>88</td>
              <td>85</td>
              <td>83</td>
            </tr>
          </table>
          `,
          tooltipStyle: {
            iPad: '{ "max-width": "940px" }',
            iPadPro: '{ "max-width": "1010px" }',
          },
          components: ['toggles'],
        },
        {
          name: 'Vital Signs: 24 h DBP',
          tileId: 'tones-3-vital-signs-24h-dbp',
          content: [
            {
              id: 'tones-3-24h-dbp',
              type: 'line',
            },
          ],
          tooltip: `
          <p>Sample size values for effects of solriamfetol on DBP as measured by 24 h ABPM<sup>19</sup></p>

          <table style="width: 100%">
          <tr>
            <td>Treatment Group</td>
            <td style="width: 6.7%">6 AM - 8 AM</td>
            <td style="width: 6.7%">8 AM - 10 AM</td>
            <td style="width: 6.7%">10 AM - 12 PM</td>
            <td style="width: 6.7%">12 PM - 2 PM</td>
            <td style="width: 6.7%">2 PM - 4 PM</td>
            <td style="width: 6.7%">4 PM - 6 PM</td>
            <td style="width: 6.7%">6 PM - 8 PM</td>
            <td style="width: 6.7%">8 PM - 10 PM</td>
            <td style="width: 6.7%">10 PM - 12 AM</td>
            <td style="width: 6.7%">12 AM - 2 AM</td>
            <td style="width: 6.7%">2 AM - 4 AM</td>
            <td style="width: 6.7%">4 AM - 6 AM</td>
          </tr>
          <tr>
            <td>Placebo at screening, n</td>
            <td>116</td>
            <td>116</td>
            <td>115</td>
            <td>116</td>
            <td>116</td>
            <td>113</td>
            <td>115</td>
            <td>113</td>
            <td>116</td>
            <td>116</td>
            <td>116</td>
            <td>114</td>
          </tr>
          <tr>
            <td>Placebo at week 8, n</td>
            <td>92</td>
            <td>92</td>
            <td>94</td>
            <td>94</td>
            <td>93</td>
            <td>91</td>
            <td>93</td>
            <td>91</td>
            <td>93</td>
            <td>94</td>
            <td>93</td>
            <td>92</td>
          </tr>
          <tr>
            <td>Solriamfetol 37.5 mg at screening, n</td>
            <td>56</td>
            <td>55</td>
            <td>53</td>
            <td>54</td>
            <td>55</td>
            <td>55</td>
            <td>53</td>
            <td>55</td>
            <td>57</td>
            <td>57</td>
            <td>57</td>
            <td>57</td>
          </tr>
          <tr>
            <td>Solriamfetol 37.5 mg at week 8, n</td>
            <td>39</td>
            <td>40</td>
            <td>42</td>
            <td>43</td>
            <td>43</td>
            <td>42</td>
            <td>43</td>
            <td>42</td>
            <td>43</td>
            <td>42</td>
            <td>41</td>
            <td>42</td>
          </tr>
          <tr>
            <td>Solriamfetol 75 mg at screening, n</td>
            <td>58</td>
            <td>59</td>
            <td>59</td>
            <td>59</td>
            <td>60</td>
            <td>60</td>
            <td>61</td>
            <td>60</td>
            <td>61</td>
            <td>59</td>
            <td>60</td>
            <td>58</td>
          </tr>
          <tr>
            <td>Solriamfetol 75 mg at week 8, n</td>
            <td>51</td>
            <td>50</td>
            <td>51</td>
            <td>47</td>
            <td>49</td>
            <td>48</td>
            <td>47</td>
            <td>47</td>
            <td>48</td>
            <td>49</td>
            <td>49</td>
            <td>49</td>
          </tr>
          <tr>
            <td>Solriamfetol 150 mg at screening, n</td>
            <td>108</td>
            <td>109</td>
            <td>111</td>
            <td>112</td>
            <td>112</td>
            <td>114</td>
            <td>113</td>
            <td>113</td>
            <td>114</td>
            <td>113</td>
            <td>111</td>
            <td>111</td>
          </tr>
          <tr>
            <td>Solriamfetol 150 mg at week 8, n</td>
            <td>97</td>
            <td>97</td>
            <td>97</td>
            <td>98</td>
            <td>98</td>
            <td>97</td>
            <td>100</td>
            <td>102</td>
            <td>100</td>
            <td>99</td>
            <td>99</td>
            <td>99</td>
          </tr>
          <tr>
            <td>Solriamfetol 300 mg at screening, n</td>
            <td>107</td>
            <td>107</td>
            <td>106</td>
            <td>109</td>
            <td>109</td>
            <td>111</td>
            <td>108</td>
            <td>109</td>
            <td>109</td>
            <td>108</td>
            <td>107</td>
            <td>109</td>
          </tr>
          <tr>
            <td>Solriamfetol 300 mg at week 8, n</td>
            <td>87</td>
            <td>84</td>
            <td>84</td>
            <td>87</td>
            <td>88</td>
            <td>87</td>
            <td>86</td>
            <td>87</td>
            <td>87</td>
            <td>88</td>
            <td>85</td>
            <td>83</td>
          </tr>
          </table>
          `,
          tooltipStyle: {
            iPad: '{ "max-width": "940px" }',
            iPadPro: '{ "max-width": "1010px" }',
          },
          components: ['toggles'],
        },
        {
          name: 'Primary Therapy Adherence',
          tileId: 'tones-3-cpap-adherence',
          content: [
            {
              id: 'tones-3-cpap-adh',
              type: 'table',
            },
          ],
          components: [],
        },
      ],
    },
  ],
};
